import { MatDialog, MatDialogConfig } from '@angular/material/dialog';

import { AuthService } from '../../core/services/http/auth/auth.service';
import { UserService } from '../../core/services/user/user.service';
import { UserRole } from '../configs/user-role.config';

import { MasterDataService } from 'src/app/core/services/http/master-data/master-data.service';
import { PermissionsService } from 'src/app/core/services/http/permissions/permissions.service';
import { TodoDialogueComponent } from 'src/app/features/dashboard/pages/dashboard/todo-dialogue/todo-dialogue/todo-dialogue.component';

import { JwtHelperService } from '@auth0/angular-jwt';

/** localStorage key for the signed permission token. Persists while logged in (survives tab/browser
 * close), no time expiry — cleared only on logout. */
const PERMISSION_TOKEN_KEY = 'np_permission_token';

/**
 * Sets the user details in user service (if auth token is available) during app initialization
 * @param authService - auth service to get access token
 * @param userService - user service to set user details from token
 *
 * @returns promise
 */
function setUserDetails(
  authService: AuthService,
  userService: UserService,
  masterService: MasterDataService,
  matDialog: MatDialog
): Promise<boolean> {
  return new Promise(resolve => {
    const accessToken = authService.accessToken;
    if (accessToken) {
      const jwtHelperService = new JwtHelperService();
      let userDetails = jwtHelperService.decodeToken(accessToken);
      // Normalise Cognito access-token claims to the Keycloak shape the app expects, so existing
      // readers (preferred_username, resource_access.nephroplus-bo.roles) keep working unchanged.
      if (userDetails) {
        if (userDetails.preferred_username == null && userDetails.username != null) {
          userDetails.preferred_username = userDetails.username;
        }
        if (userDetails.resource_access == null) {
          userDetails.resource_access = { 'nephroplus-bo': { roles: userDetails['cognito:groups'] || [] } };
        }
      }
      // userDetails = {
      //     "exp": 1735559079,
      //     "iat": 1735558179,
      //     "auth_time": 1735558179,
      //     "jti": "67a7a0c3-f236-4e27-b121-d993fa3ab4c6",
      //     "iss": "https://uat-bo.nephroplus.com/keycloak/realms/nephroplus",
      //     "aud": "nephroplus-bo",
      //     "sub": "f31fcb60-ba44-469f-8591-243c94b18199",
      //     "typ": "Bearer",
      //     "azp": "nephroplus-bo",
      //     "session_state": "9b9b6173-0dc3-4ce7-aeb2-2e7cc8aac459",
      //     "acr": "1",
      //     "realm_access": {
      //       "roles": [
      //         "offline_access",
      //         "default_role_nephroplus",
      //         "uma_authorization"
      //       ]
      //     },
      //     "organization": {
      //         "staffNPId": "NP506048"
      //     },
      //     "resource_access": {
      //       "nephroplus-bo": {
      //         "roles": [
      //           "Admin",
      //           "Center Manager"
      //         ]
      //       },
      //       "account": {
      //         "roles": [
      //           "manage-account",
      //           "manage-account-links",
      //           "view-profile"
      //         ]
      //       }
      //     },
      //     "authorization": {
      //       "permissions": [
      //         {
      //           "scopes": [
      //             "view_quality_audit",
      //             "view_assigned_center",
      //             "view_grn",
      //             "system_actions",
      //             "edit_preauth",
      //             "view_own_center_service_item",
      //             "create_ops_daily_report",
      //             "view_viral_marker_report",
      //             "view_watchlist_ticket",
      //             "view_guest_reports",
      //             "view_bihar_bill_list",
      //             "create_ops_audit",
      //             "view_mobile_api",
      //             "create_quality_audit",
      //             "view_bihar_invoice_summary",
      //             "view_ops_audit",
      //             "view_session_schedule",
      //             "create_adverse_event",
      //             "view_detailed_invoice_summary",
      //             "view_own_center_rateplan",
      //             "create_guest_account",
      //             "create_quality_report",
      //             "bihar_guest_image_upload",
      //             "view_bihar_invoice",
      //             "comment_ticket_center_specific",
      //             "view_bihar_bill_summary",
      //             "view_cash_deposit",
      //             "create_quality_menu_page",
      //             "create_session_schedule",
      //             "view_guest_profile",
      //             "create_investigation",
      //             "edit_guest_account",
      //             "service_wise_item_count_report",
      //             "nullify_invoice",
      //             "view_preauth",
      //             "comment_ticket",
      //             "print_grn",
      //             "reset_password",
      //             "create_preauth",
      //             "create_clinical_menu_page",
      //             "edit_ticket",
      //             "view_dialysate_report",
      //             "view_own_ticket",
      //             "edit_user_for_assigned_center",
      //             "approve_bihar_guest_sessions",
      //             "view_ops_daily_report",
      //             "view_bills",
      //             "view_tr",
      //             "backdated_session",
      //             "view_po",
      //             "view_assigned_center_user_profile",
      //             "edit_assigned_center",
      //             "view_deposit_utlization_report",
      //             "view_ops_menu_page",
      //             "guest-easy-edit",
      //             "view_investigation",
      //             "debit_note",
      //             "create_user_for_assigned_center",
      //             "email_bill",
      //             "view_daily_quality_report",
      //             "update_guest_profile",
      //             "nullify_bills",
      //             "bihar_guest_coupon_image_upload",
      //             "create_dialysate_quality_report",
      //             "view_own_center_detail",
      //             "auto_deduction_inventory",
      //             "create_prescription",
      //             "pending_approval_guest_report",
      //             "view_dialysate_quality_report",
      //             "create_bill",
      //             "print_bill",
      //             "view_user_profile",
      //             "view_ticket",
      //             "view_package_utilization_report",
      //             "create_daily_quality_report",
      //             "create_dialysate_report",
      //             "view_operations_report_menu",
      //             "view_revenue_related_page",
      //             "sms_bill",
      //             "create_cash_deposit",
      //             "view_adverse_event",
      //             "print_bihar_invoice",
      //             "view_assigned_ticket",
      //             "view_center_clinical_report"
      //           ],
      //           "rsid": "b7b4cd0b-f971-4aa6-95b0-b16bf30a1cfe",
      //           "rsname": "bo"
      //         }
      //       ]
      //     },
      //     "scope": "openid profile email",
      //     "sid": "9b9b6173-0dc3-4ce7-aeb2-2e7cc8aac459",
      //     "email_verified": false,
      //     "name": "The Administrator",
      //     // "preferred_username": "admin",
      //     "preferred_username": "NP179179",
      //     "given_name": "The Administrator",
      //     "family_name": "",
      //     "email": "admin@nephroplus.com"
      //   }
      userService.setUserDetails$(userDetails);
      const boRoles = userDetails?.resource_access?.['nephroplus-bo']?.roles;
      if (Array.isArray(boRoles) && boRoles.includes(UserRole.centerManager)) {
        masterService.fetchToDoValidation().subscribe(res => {
          let isPending = false;
          if (res.data.summary.find((obj: any) => obj.count > 0) && !res.data.bypass) {
            isPending = true;
          }
          res.data.isPending = isPending;
          // res.data.bypassCount = res.data.summary.reduce((acc: any, obj: any) => acc + obj.count, 0);
          userService.setToDoDetails$(res.data);
          const dialogConfig = new MatDialogConfig();
          dialogConfig.backdropClass = 'blurred-backdrop';
          dialogConfig.data = res?.data;
          dialogConfig.width = '500px';
          dialogConfig.disableClose = true;
    
          if (!res?.data?.bypass && res?.data?.isPending) {
            if (!userService.isTodoDialogOpened) {
              matDialog.open(TodoDialogueComponent, dialogConfig);
              userService.setTODODialogState(true);
            }
          }
        });
      }
    }

    return resolve(true);
  });
}


function applyPermissionToken(token: string, userService: UserService): void {
  try {
    const payload = new JwtHelperService().decodeToken(token);
    userService.setUserPermissions(payload?.permissions || []);
  } catch {
    userService.setUserPermissions([]);
  }
}

function loadPermissions(
  authService: AuthService,
  userService: UserService,
  permissionsService: PermissionsService
): Promise<void> {
  if (!authService.isLoggedIn) {
    return Promise.resolve();
  }

  const cachedToken = localStorage.getItem(PERMISSION_TOKEN_KEY);
  if (cachedToken) {
    applyPermissionToken(cachedToken, userService);

    return Promise.resolve();
  }

  return permissionsService
    .getMyPermissions()
    .toPromise()
    .then((res: any) => {
      const token = res?.data?.permission_token;
      if (token) {
        localStorage.setItem(PERMISSION_TOKEN_KEY, token);
        applyPermissionToken(token, userService);
      }
    })
    .catch(() => undefined);
}

export function appInitializer(
  authService: AuthService,
  userService: UserService,
  masterService: MasterDataService,
  permissionsService: PermissionsService,
  dialog: MatDialog
): () => Promise<any> {
  return () =>
    authService
      .handleCallback()
      .then(() => authService.ensureValidAccessToken())
      .then(() => setUserDetails(authService, userService, masterService, dialog))
      .then(() => loadPermissions(authService, userService, permissionsService));
}
